import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apisApi } from '@/api/apis-api'
import { clientsApi } from '@/api/clients-api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import type { OIDCApi } from '@/types/api'
import type { OIDCClient } from '@/types/client'

export default function ApisPage() {
  const { toast } = useToast()
  const [apiNameInput, setApiNameInput] = useState('')
  const [apiIdInput, setApiIdInput] = useState('')
  
  // API state for client operations
  const [currentApi, setCurrentApi] = useState<OIDCApi | null>(null)
  
  // Add client to API dialog
  const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')
  
  // Fetch APIs
  const { data: apisData, isLoading: apisLoading, error: apisError, refetch: refetchApis } = useQuery({
    queryKey: ['apis'],
    queryFn: () => apisApi.getApis(),
  })

  // Fetch clients for dropdown and displaying client names in the allowed clients list
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getClients(),
    // Ensure clients data is available when displaying the allowed clients
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const handleCreateApi = async () => {
    try {
      if (!apiNameInput || !apiIdInput) {
        toast({
          title: 'Missing Information',
          description: 'API Name and API ID are required',
          variant: 'destructive',
        })
        return
      }

      await apisApi.createApi({
        apiName: apiNameInput,
        apiId: apiIdInput,
        allowedClients: [],
      })

      toast({
        title: 'Success',
        description: `API ${apiNameInput} created successfully`,
      })

      // Reset form and refresh data
      setApiNameInput('')
      setApiIdInput('')
      refetchApis()
    } catch (error) {
      console.error('Error creating API:', error)
      toast({
        title: 'Error',
        description: 'Failed to create API. Please try again.',
        variant: 'destructive',
      })
    }
  }


  const handleDeleteApi = async (apiId: string) => {
    try {
      await apisApi.deleteApi(apiId)
      toast({
        title: 'Success',
        description: `API deleted successfully`,
      })
      refetchApis()
    } catch (error) {
      console.error('Error deleting API:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete API. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleOpenAddClientDialog = (api: OIDCApi) => {
    setCurrentApi(api)
    setSelectedClientId('')
    setIsAddClientDialogOpen(true)
  }

  const handleAddClientToApi = async () => {
    if (!currentApi || !selectedClientId) return

    try {
      await apisApi.addClientToApi(currentApi.apiId, selectedClientId)
      toast({
        title: 'Success',
        description: `Client added to API successfully`,
      })
      setIsAddClientDialogOpen(false)
      refetchApis()
    } catch (error) {
      console.error('Error adding client to API:', error)
      toast({
        title: 'Error',
        description: 'Failed to add client to API. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleRemoveClientFromApi = async (apiId: string, clientId: string) => {
    try {
      await apisApi.removeClientFromApi(apiId, clientId)
      toast({
        title: 'Success',
        description: `Client removed from API successfully`,
      })
      refetchApis()
    } catch (error) {
      console.error('Error removing client from API:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove client from API. Please try again.',
        variant: 'destructive',
      })
    }
  }

  // Get available clients (not already added to the API)
  const getAvailableClients = (currentApi: OIDCApi | null) => {
    if (!clientsData?.items || !currentApi) return []
    return clientsData.items.filter(
      (client: OIDCClient) => !currentApi.allowedClients.includes(client.client_id)
    )
  }

  if (apisLoading || clientsLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">OIDC APIs</h1>
        </div>
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Existing APIs</h2>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </Card>
      </div>
    )
  }

  if (apisError) {
    return (
      <div className="text-red-500 p-8" role="alert" aria-live="assertive">
        Error loading APIs: {apisError.message}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center pb-4">
        <h1 className="text-2xl font-bold">OIDC APIs</h1>
      </div>
      <Separator className="mb-6" />

      {/* Create API Form */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4" id="create-api-heading">Create New API</h2>
        <form 
          className="grid gap-4 md:grid-cols-3"
          aria-labelledby="create-api-heading"
          onSubmit={(e) => { e.preventDefault(); handleCreateApi(); }}
        >
          <div className="space-y-2">
            <Label htmlFor="api-id">API ID</Label>
            <Input
              id="api-id"
              value={apiIdInput}
              onChange={(e) => setApiIdInput(e.target.value)}
              placeholder="Enter API ID"
              aria-required="true"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="api-name">API Name</Label>
            <Input
              id="api-name"
              value={apiNameInput}
              onChange={(e) => setApiNameInput(e.target.value)}
              placeholder="Enter API name"
              aria-required="true"
              required
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" aria-label="Create new API">
              Create API
            </Button>
          </div>
        </form>
      </Card>

      {/* APIs List */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Existing APIs</h2>
        {apisData?.items && apisData.items.length > 0 ? (
          <ScrollArea className="h-[500px]">
            <div className="space-y-6">
              {apisData.items.map((api: OIDCApi) => (
                <div key={api.apiId} className="border border-border p-5 rounded-md shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-medium">{api.apiName}</h3>
                        <Badge variant="outline" className="font-mono text-xs">{api.apiId}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Created: {new Date(api.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDeleteApi(api.apiId)}
                        aria-label={`Delete API ${api.apiName}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium" id={`allowed-clients-heading-${api.apiId}`}>Allowed Clients</h4>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenAddClientDialog(api)}
                        aria-label={`Add client to API ${api.apiName}`}
                      >
                        Add Client
                      </Button>
                    </div>
                    
                    {api.allowedClients.length > 0 ? (
                      <div className="space-y-2" aria-labelledby={`allowed-clients-heading-${api.apiId}`} role="group">
                      <Table>
                          <TableBody>
                            {api.allowedClients.map((clientId) => {
                              // Find client name from the clientsData
                              const clientInfo = clientsData?.items?.find(
                                (client: OIDCClient) => client.client_id === clientId
                              );
                              return (
                                <TableRow key={clientId}>
                                  <TableCell className="py-2">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="secondary" className="font-mono">{clientId}</Badge>
                                        {clientInfo ? (
                                          <span className="text-sm">
                                            {clientInfo.client_name}
                                          </span>
                                        ) : (
                                          <span className="text-sm text-muted-foreground italic">
                                            Unknown client
                                          </span>
                                        )}
                                      </div>
                                      {clientInfo?.client_description && (
                                        <span className="text-xs text-muted-foreground">
                                          {clientInfo.client_description}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right py-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleRemoveClientFromApi(api.apiId, clientId)}
                                      aria-label={`Remove client ${clientId} from API ${api.apiName}`}
                                    >
                                      Remove
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md text-center">
                        No clients allowed for this API.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
            <div className="text-muted-foreground">No APIs found.</div>
            <div>Create your first API using the form above.</div>
          </div>
        )}
      </Card>


      {/* Add Client to API Dialog */}
      {currentApi && (
        <Dialog open={isAddClientDialogOpen} onOpenChange={setIsAddClientDialogOpen}>
          <DialogContent aria-labelledby="add-client-dialog-title" aria-describedby="add-client-dialog-description">
            <DialogHeader>
              <DialogTitle id="add-client-dialog-title">Add Client to API</DialogTitle>
              <DialogDescription id="add-client-dialog-description">
                Select a client to add to the API '{currentApi.apiName}'.
              </DialogDescription>
            </DialogHeader>
            <form 
              className="grid gap-4 py-4" 
              onSubmit={(e) => { e.preventDefault(); handleAddClientToApi(); }}
            >
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="select-client" className="text-right">Client</Label>
                <div className="col-span-3">
                  <Select 
                    value={selectedClientId} 
                    onValueChange={setSelectedClientId}
                    aria-required="true"
                  >
                    <SelectTrigger aria-label="Select a client to add">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientsLoading ? (
                        <SelectItem value="loading" disabled>Loading clients...</SelectItem>
                      ) : getAvailableClients(currentApi).length > 0 ? (
                        getAvailableClients(currentApi).map((client: OIDCClient) => (
                          <SelectItem key={client.client_id} value={client.client_id}>
                            {client.client_name} ({client.client_id})
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>No available clients</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </form>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddClientDialogOpen(false)} type="button" aria-label="Cancel adding client">Cancel</Button>
              <Button 
                onClick={handleAddClientToApi}
                disabled={!selectedClientId || selectedClientId === 'loading' || selectedClientId === 'none'}
                type="submit"
                aria-label="Add the selected client to this API"
              >
                Add Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}